import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Shield, AlertTriangle, Scale, TrendingDown, Brain, CheckCircle, 
  Rocket, Lock, Target, Clock, ArrowRight, Building2, Users,
  FileWarning, Gavel, ShieldCheck, Eye, MessageSquareWarning
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function SolucaoBrasilPage() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Schema.org FAQ for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Canal de denúncias é obrigatório para empresas no Brasil?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Embora não seja obrigatório para todas as empresas, a legislação brasileira exige canais de denúncia para determinados setores e portes. Além disso, em processos trabalhistas e auditorias, a existência de um canal estruturado é considerada evidência de boa-fé e governança."
        }
      },
      {
        "@type": "Question",
        "name": "Como funciona um canal de denúncias para empresas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O canal de denúncias permite que colaboradores, parceiros e terceiros reportem irregularidades de forma segura e, quando desejado, anônima. As denúncias são registradas, tratadas e documentadas, criando histórico defensável para a empresa."
        }
      },
      {
        "@type": "Question",
        "name": "Pequenas empresas precisam de canal de denúncias?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim. PMEs são ainda mais vulneráveis a riscos internos por não possuírem estrutura jurídica ou de compliance. Um único problema não documentado pode ter impacto desproporcional no negócio."
        }
      }
    ]
  };

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "OmbudNow",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "Canal de denúncias online para empresas brasileiras. Plataforma segura de compliance e ouvidoria corporativa.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "BRL"
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e]">
      <Helmet>
        <title>Canal de Denúncias para Empresas | Compliance Empresarial | OmbudNow</title>
        <meta name="description" content="Canal de denúncias online para empresas brasileiras. Proteja sua SMB contra riscos legais, trabalhistas e reputacionais. Plataforma segura, anônima e adequada à legislação." />
        <meta name="keywords" content="canal de denúncias, canal de denúncias para empresas, compliance empresarial, ouvidoria corporativa, denúncia anônima, canal de ética, compliance PME, canal de denúncias online" />
        <link rel="canonical" href="https://ombudnow.com/solucaoBrasil" />
        <meta property="og:title" content="Canal de Denúncias para Empresas | OmbudNow" />
        <meta property="og:description" content="Proteja sua empresa contra riscos legais e trabalhistas com um canal de denúncias profissional." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ombudnow.com/solucaoBrasil" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#1a1a2e]/95 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#f5a623] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#1a1a2e]" />
              </div>
              <span className="text-xl font-bold text-white">OmbudNow</span>
            </Link>
            <Link to="/pt">
              <Button className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] font-semibold">
                Criar meu canal
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="px-6 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#f5a623]/20 text-[#f5a623] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Lock className="w-4 h-4" />
              Canal de Denúncias para Empresas
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Proteja sua empresa contra riscos legais, trabalhistas e reputacionais
            </h1>
            
            <p className="text-xl text-[#f5a623] font-medium mb-4">
              OmbudNow — canal de denúncias online simples, seguro e adequado à legislação brasileira
            </p>
            
            <p className="text-lg text-slate-300 mb-4 leading-relaxed">
              A maioria das pequenas e médias empresas no Brasil acredita que está protegida.
            </p>
            <p className="text-lg text-slate-400 mb-8 leading-relaxed">
              Na prática, milhares operam hoje com <strong className="text-white">riscos ocultos</strong>, sem qualquer mecanismo formal de prevenção, denúncia ou registro.
            </p>
            
            <p className="text-slate-300 mb-10 max-w-3xl mx-auto">
              O OmbudNow permite implementar um <strong className="text-white">canal de denúncias corporativo</strong> real, defensável e acessível, mesmo sem departamento jurídico ou estrutura de compliance.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pt">
                <Button size="lg" className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] text-lg px-8 font-semibold h-14 w-full sm:w-auto">
                  <Lock className="w-5 h-5 mr-2" />
                  Criar canal de denúncias agora
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 text-lg px-8 h-14"
                onClick={() => document.getElementById('riscos').scrollIntoView({ behavior: 'smooth' })}
              >
                <AlertTriangle className="w-5 h-5 mr-2" />
                Minha empresa corre risco?
              </Button>
            </div>
          </div>
        </section>

        {/* Risk Section */}
        <section id="riscos" className="px-6 py-16 bg-gradient-to-b from-red-950/30 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O risco que a maioria das empresas só descobre tarde demais
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-6">
              Empresas não quebram apenas por crises externas.<br />
              <strong className="text-white">Elas quebram por problemas internos que nunca foram denunciados.</strong>
            </p>
            
            <p className="text-slate-400 mb-8">Na prática, quase toda empresa já enfrentou — ou enfrentará — situações como:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { icon: MessageSquareWarning, text: "Assédio moral ou sexual não reportado" },
                { icon: Users, text: "Condutas antiéticas de liderança" },
                { icon: FileWarning, text: "Fraudes internas de pequeno valor que crescem em silêncio" },
                { icon: Gavel, text: "Conflitos trabalhistas mal documentados" },
                { icon: Eye, text: "Funcionários com medo de falar" },
                { icon: Scale, text: "Denúncias feitas diretamente ao sindicato, advogado ou Justiça" },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-red-500/20">
                  <item.icon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-red-950/50 rounded-2xl p-6 border border-red-500/30 mb-8">
              <p className="text-white text-lg font-medium mb-2">O problema não é o erro.</p>
              <p className="text-slate-300">O problema é <strong className="text-red-400">não conseguir provar que a empresa tentou prevenir</strong>.</p>
            </div>
            
            <Link to="/pt/guias/canal-denuncias-ouvidoria-obrigatorio-smb" className="inline-flex items-center gap-2 text-[#f5a623] hover:underline font-medium">
              📘 Entender minhas obrigações legais
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Legal Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/20 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Canal de denúncias e a legislação brasileira
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-4">
              A legislação trabalhista e de compliance no Brasil evoluiu.
            </p>
            <p className="text-slate-400 mb-8">
              Hoje, empresas são avaliadas não apenas pelo que aconteceu, mas por <strong className="text-white">o que foi feito para evitar</strong>.
            </p>
            
            <div className="bg-[#0EA5E9]/10 rounded-2xl p-6 border border-[#0EA5E9]/30 mb-8">
              <p className="text-slate-300 mb-4">Em fiscalizações, processos e auditorias, a pergunta central é:</p>
              <p className="text-xl text-white font-semibold italic">
                "A empresa possuía um canal de denúncias estruturado?"
              </p>
            </div>
            
            <p className="text-slate-400 mb-4">Sem um <strong className="text-white">canal de denúncias para empresas</strong>, não há como comprovar:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Boa-fé preventiva",
                "Governança mínima",
                "Esforço real de mitigação de riscos",
                "Tratamento adequado de ocorrências"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-[#0EA5E9]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <p className="text-red-400 font-medium mb-8">
              A ausência desse canal pode ser interpretada como omissão ou negligência organizacional.
            </p>
            
            <Link to="/pt">
              <Button className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white font-semibold">
                <ShieldCheck className="w-5 h-5 mr-2" />
                Proteger minha empresa agora
              </Button>
            </Link>
          </div>
        </section>

        {/* Cost Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-orange-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O custo real de não ter um canal de denúncias
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-6">Quando não existe um canal formal:</p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-3">
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> A denúncia não chega à empresa
                </p>
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> Ela chega diretamente ao Judiciário
                </p>
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> Não há registro interno
                </p>
              </div>
              <div className="space-y-3">
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> Não há histórico
                </p>
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> Não há defesa estruturada
                </p>
              </div>
            </div>
            
            <p className="text-slate-300 mb-6">O resultado é previsível:</p>
            
            <div className="flex flex-wrap gap-3 mb-8">
              {["Processos trabalhistas", "Multas", "Exposição dos sócios", "Danos reputacionais", "Perda de contratos B2B"].map((item, index) => (
                <span key={index} className="bg-red-500/20 text-red-300 px-4 py-2 rounded-full text-sm font-medium">
                  {item}
                </span>
              ))}
            </div>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
              <p className="text-white text-lg font-medium italic">
                "Nunca fomos informados" <span className="text-red-400">não é defesa jurídica</span>.
              </p>
            </div>
          </div>
        </section>

        {/* SMB Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Por que SMBs sofrem mais com riscos de compliance
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-green-500/10 rounded-2xl p-6 border border-green-500/30">
                <h3 className="text-white font-semibold mb-4">Grandes empresas contam com:</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Jurídico interno</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Compliance estruturado</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Consultorias especializadas</li>
                </ul>
              </div>
              
              <div className="bg-red-500/10 rounded-2xl p-6 border border-red-500/30">
                <h3 className="text-white font-semibold mb-4">Pequenas e médias empresas contam com:</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> O sócio</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> O contador</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Decisões reativas</li>
                </ul>
              </div>
            </div>
            
            <p className="text-slate-300 text-lg">
              Isso faz com que um único problema interno tenha <strong className="text-white">impacto desproporcional</strong> no caixa, na reputação e na continuidade do negócio.
            </p>
            
            <Link to="/pt/guias/diferenca-canal-denuncias-ouvidoria" className="inline-flex items-center gap-2 text-[#f5a623] hover:underline font-medium mt-6">
              🔍 Ver como um canal de denúncias reduz esses riscos
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Product Section */}
        <section className="px-6 py-16 bg-gradient-to-b from-[#f5a623]/10 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#f5a623]/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#f5a623]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O que é o OmbudNow e como ele protege sua empresa
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-8">
              O OmbudNow é uma plataforma de <strong className="text-white">canal de denúncias online</strong> criada especificamente para SMBs que precisam:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                "Reduzir riscos legais e trabalhistas",
                "Proteger sócios e gestores",
                "Demonstrar governança e boa-fé",
                "Atender exigências legais sem burocracia"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <Target className="w-5 h-5 text-[#f5a623]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-300 mb-6">Com o OmbudNow, sua empresa passa a ter:</p>
            
            <div className="bg-white/5 rounded-2xl p-8 border border-[#f5a623]/30 mb-8">
              <div className="space-y-4">
                {[
                  "Canal de denúncias corporativo seguro",
                  "Possibilidade de denúncias anônimas",
                  "Registro estruturado e rastreável",
                  "Histórico defensável para auditorias e processos",
                  "Ferramenta real de compliance empresarial"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#22c55e]" />
                    <span className="text-white font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Link to="/pt">
              <Button size="lg" className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] text-lg px-8 font-semibold">
                <Lock className="w-5 h-5 mr-2" />
                Ativar canal de denúncias agora
              </Button>
            </Link>
          </div>
        </section>

        {/* Insurance Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#22c55e]/20 rounded-xl flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-[#22c55e]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Canal de denúncias não é burocracia. É seguro empresarial.
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-8">Um canal de denúncias bem implementado:</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {[
                "Reduz passivos trabalhistas",
                "Evita escalada de conflitos",
                "Protege a reputação da empresa",
                "Fortalece a confiança de clientes e parceiros",
                "Facilita contratos com empresas maiores"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-[#22c55e]/10 rounded-xl p-4 border border-[#22c55e]/30">
                  <CheckCircle className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{item}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
              <p className="text-white text-lg">
                <strong className="text-[#22c55e]">Empresas maduras se antecipam.</strong><br />
                <span className="text-slate-400">Empresas reativas pagam o preço depois.</span>
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/20 rounded-xl flex items-center justify-center">
                <Rocket className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Como implementar um canal de denúncias na sua empresa
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-8">Com o OmbudNow, o processo é simples:</p>
            
            <div className="space-y-4 mb-10">
              {[
                { step: "1️⃣", text: "Criar sua conta" },
                { step: "2️⃣", text: "Configurar o canal em minutos" },
                { step: "3️⃣", text: "Comunicar colaboradores e parceiros" },
                { step: "4️⃣", text: "Receber e gerenciar denúncias com segurança" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                  <span className="text-2xl">{item.step}</span>
                  <span className="text-white text-lg">{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-4 text-slate-400 mb-8">
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem consultorias caras</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem departamento jurídico</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem complexidade</span>
            </div>
            
            <Link to="/pt">
              <Button size="lg" className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white text-lg px-8 font-semibold">
                <Rocket className="w-5 h-5 mr-2" />
                Implementar canal de denúncias hoje
              </Button>
            </Link>
          </div>
        </section>

        {/* Security Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#f5a623]/20 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6 text-[#f5a623]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Segurança, confidencialidade e confiança
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[
                { icon: Shield, text: "Ambiente seguro" },
                { icon: Lock, text: "Controle de acesso" },
                { icon: Eye, text: "Confidencialidade total" },
                { icon: ShieldCheck, text: "Proteção de dados" },
                { icon: Users, text: "Anonimato opcional para denunciantes" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
                  <item.icon className="w-5 h-5 text-[#f5a623]" />
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-400 italic">
              Porque sem confiança, não existe denúncia.
            </p>
          </div>
        </section>

        {/* Target Audience Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Para quem o OmbudNow é indicado
              </h2>
            </div>
            
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                "Pequenas e médias empresas",
                "Startups em crescimento",
                "Empresas de serviços",
                "Indústrias",
                "Escritórios",
                "Negócios B2B"
              ].map((item, index) => (
                <span key={index} className="bg-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-sm font-medium">
                  {item}
                </span>
              ))}
            </div>
            
            <p className="text-slate-300 text-lg">
              Se sua empresa possui funcionários, parceiros ou prestadores, <strong className="text-white">ter um canal de denúncias não é opcional</strong>.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="px-6 py-20 bg-gradient-to-t from-red-950/30 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                A pergunta que todo empresário deveria se fazer
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-6">
              Se amanhã surgir uma denúncia grave, sua empresa consegue provar que:
            </p>
            
            <div className="space-y-3 mb-8">
              {[
                "Oferecia um canal de denúncias formal?",
                "Incentivava o reporte interno?",
                "Registrava e tratava ocorrências?",
                "Agia com governança e boa-fé?"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <span className="text-[#f5a623]">❓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-red-950/50 rounded-2xl p-6 border border-red-500/30 mb-10">
              <p className="text-white text-lg">
                Se a resposta for <strong className="text-red-400">"não"</strong>, o risco já existe hoje.
              </p>
            </div>
            
            <div className="text-center">
              <Link to="/pt">
                <Button size="lg" className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] text-xl px-12 font-bold h-16">
                  🚨 Criar canal de denúncias agora
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Related Solutions Section */}
      <section className="px-6 py-12 bg-white/[0.02] border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-white mb-6 text-center">Soluções Relacionadas</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/solucaobrasilouvidoria" className="group bg-white/5 rounded-xl p-5 border border-white/10 hover:border-[#0EA5E9]/50 transition-all">
              <div className="flex items-center gap-3 mb-2">
                <MessageSquareWarning className="w-5 h-5 text-[#0EA5E9]" />
                <span className="text-white font-medium group-hover:text-[#0EA5E9] transition-colors">Ouvidoria Corporativa</span>
              </div>
              <p className="text-slate-400 text-sm">Transforme reclamações em gestão e melhoria contínua</p>
            </Link>
            <Link to="/solucaobrasilcompliance" className="group bg-white/5 rounded-xl p-5 border border-white/10 hover:border-[#22c55e]/50 transition-all">
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="w-5 h-5 text-[#22c55e]" />
                <span className="text-white font-medium group-hover:text-[#22c55e] transition-colors">Compliance Empresarial</span>
              </div>
              <p className="text-slate-400 text-sm">Proteja sua empresa com governança corporativa</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#f5a623] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#1a1a2e]" />
              </div>
              <span className="text-xl font-bold text-white">OmbudNow</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <Link to="/pt/sobre" className="text-slate-400 hover:text-white transition-colors">Sobre</Link>
              <Link to="/pt/contato" className="text-slate-400 hover:text-white transition-colors">Contato</Link>
              <Link to="/pt/guias" className="text-slate-400 hover:text-white transition-colors">Guias</Link>
              <Link to="/pt/politica-de-privacidade" className="text-slate-400 hover:text-white transition-colors">Privacidade</Link>
              <Link to="/pt/termos-de-uso" className="text-slate-400 hover:text-white transition-colors">Termos</Link>
            </div>
            
            <p className="text-slate-500 text-sm">
              © 2025 OmbudNow. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
